import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Stack,
  Divider,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder,
  Phone,
  Email,
  Language,
  LocationOn,
  LinkedIn,
  DriveEta,
  Link as LinkIcon,
} from "@mui/icons-material";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: "#084c34", label: "Forest Green" },
    { value: "#1a2a6c", label: "Navy Blue" },
    { value: "#b22222", label: "Deep Red" },
    { value: "#2d3436", label: "Graphite" },
    { value: "#323b4c", label: "Slate Gray" },
  ]
};

const PAGE_SIZES = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const defaultData = {
  firstName: "OLIVIA",
  lastName: "WILSON",
  currentPosition: "Marketing Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St., Any City",
  website: "reallygreatsite.com",
  profile: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc sit amet sem nec risus egestas accumsan. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
};

const defaultLeftSectionOrder = [
  'workExperience',
  'certifications',
  'accomplishments',
  'volunteering',
  'interests',
  'additionalInfo',
  'references'
];

const defaultRightSectionOrder = [
  'contact',
  'education',
  'languages',
  'skills', 
  'websites'
];

const overflowText = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  minWidth: 0,
};

const Resume40 = ({
  color = "#323b4c",
  font = "'Inter', sans-serif",
  fontSize = "14px",
  headingSize = "16px",
  fontStyle = "normal",
  sectionSpacing = 15,
  paragraphSpacing = 15,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
  const data = { ...defaultData, ...formData };

  const innerWidth = page.width - (2 * sideMargins);
  const MAIN_W = Math.round(innerWidth * 0.67);
  const SIDEBAR_W = innerWidth - MAIN_W;

  const fs      = fontSize   || "14px";
  const hs      = headingSize || "16px";
  const lh      = `${lineSpacing}px`;
  const secGap  = sectionSpacing;
  const paraGap = paragraphSpacing;
  const tbPad   = topBottomMargin;
  const sidePad = sideMargins;

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages]             = useState([]);
  const leftContentRef                = useRef(null);

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  useEffect(() => {
    try {
      const storedEntries = localStorage.getItem("educationEntries");
      if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

      const storedSkills = localStorage.getItem("skills");
      if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

      const storedSummaries = JSON.parse(localStorage.getItem("summaries") || "[]");
      if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

      setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")      || "[]"));
      setSavedLanguages(     JSON.parse(localStorage.getItem("languagesList")       || "[]"));
      setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList") || "[]"));
      setSavedCertifications( JSON.parse(localStorage.getItem("certificationsList")  || "[]"));
      setSavedReferences(     JSON.parse(localStorage.getItem("referencesList")      || "[]"));
      setSoftwareSkills(      JSON.parse(localStorage.getItem("softwareSkills")      || "[]"));
      setSavedVolunteering(   JSON.parse(localStorage.getItem("volunteering")        || "[]"));
      setSavedInterests(      JSON.parse(localStorage.getItem("interests")           || "[]"));
      
      // Load websites - handle both old and new format
      const websitesData = localStorage.getItem("websites");
      if (websitesData) {
        const parsed = JSON.parse(websitesData);
        // Ensure each website has the proper structure
        const normalized = parsed.map(item => ({
          url: item.url || item.name || item,
          label: item.label || "",
          addToHeader: item.addToHeader || false,
          name: item.name || item.url || item,
        }));
        setSavedWebsites(normalized);
      }

      const userId = localStorage.getItem("userId");
      let sectionsData = {};

      if (userId) {
        const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`) || "[]");
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) sectionsData[sectionName] = validItems;
            }
          } catch (error) {
            console.error(`Error loading custom section ${sectionName}:`, error);
          }
        });
      }
      setCustomSections(sectionsData);

      const savedLeftSectionOrder = localStorage.getItem("resume40LeftSectionOrder");
      const savedRightSectionOrder = localStorage.getItem("resume40RightSectionOrder");

      if (savedLeftSectionOrder) {
        const parsed = JSON.parse(savedLeftSectionOrder).filter(s => !s.startsWith("custom_"));
        setLeftSectionOrder(parsed);
      } else {
        setLeftSectionOrder(defaultLeftSectionOrder);
      }
      
      const customIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      if (savedRightSectionOrder) {
        const parsed = JSON.parse(savedRightSectionOrder);
        const filtered = parsed.filter(s => !s.startsWith("custom_"));
        // Ensure 'websites' is included
        if (!filtered.includes('websites')) {
          filtered.push('websites');
        }
        setRightSectionOrder([...filtered, ...customIds]);
      } else {
        // Ensure 'websites' is in the default order
        const defaultOrder = [...defaultRightSectionOrder];
        if (!defaultOrder.includes('websites')) {
          defaultOrder.push('websites');
        }
        setRightSectionOrder([...defaultOrder, ...customIds]);
      }
    } catch (e) {
      console.error("Resume40 load error:", e);
    }
  }, []);

  const formatDate = (month, year) => (month && year ? `${month} ${year}` : "");

  const mkDragStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, idx, side) => { e.preventDefault(); setDragOverIndex(idx); setDragOverSide(side); };
  const handleDragLeave = () => { setDragOverIndex(null); setDragOverSide(null); };
  const handleDragEnd   = e  => { e.currentTarget.style.opacity = "1"; setDragOverIndex(null); setDragOverSide(null); };

  const handleDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);  localStorage.setItem("resume40LeftSectionOrder",  JSON.stringify(lo));
    setRightSectionOrder(ro); localStorage.setItem("resume40RightSectionOrder", JSON.stringify(ro));
    setDragOverIndex(null); setDragOverSide(null);
    if (e.currentTarget) e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.setItem("resume40LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder));
    localStorage.setItem("resume40RightSectionOrder", JSON.stringify(defaultRightSectionOrder));
  };

  // ── Font & Heading scaling ───────────────────────────────────────────────
  const fontScale = (parseFloat(fs) || 14) / 14;
  const headingScale = (parseFloat(hs) || 16) / 16;

  const globalFontStyle = {
    fontFamily: font,
    fontStyle: fontStyle,
    lineHeight: lh,
    ...overflowText,
  };

  const getTextStyle = (additionalStyles = {}, isSidebar = false) => {
    const base = {
      ...globalFontStyle,
      color: isSidebar ? "#555" : "#333",
    };
    const scaled = { ...additionalStyles };
    if (scaled.fontSize) {
      const sizeVal = parseFloat(scaled.fontSize);
      if (!isNaN(sizeVal)) {
        scaled.fontSize = `${sizeVal * fontScale}px`;
      }
    } else {
      scaled.fontSize = `${parseFloat(fs) * fontScale}px`;
    }
    return {
      ...base,
      ...scaled
    };
  };
  
  const sectionBottomStyle = { marginBottom: `${secGap}px` };

  const renderSection = (sectionId, isSidebar = false, filterIds = null) => {
    if (filterIds !== null && !filterIds.includes(sectionId)) return null;

    const SectionBox = ({ children }) => (
      <Box
        className="resume-section"
        data-section-id={sectionId}
        style={sectionBottomStyle}
      >
        {children}
      </Box>
    );

    const SectionTitle = ({ title }) => (
      <Box style={{ marginBottom: `${Math.round(paraGap * 0.4)}px`, marginTop: `${Math.round(secGap * 0.6)}px` }}>
        <Typography
          variant="subtitle1"
          style={{
            fontWeight: 800,
            fontSize: `${parseFloat(hs) * headingScale}px`,
            color: color,
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontFamily: font,
            lineHeight: lh,
            ...overflowText
          }}
        >
          {title}
        </Typography>
        <Divider style={{ borderColor: color, borderBottomWidth: lineWeight, marginTop: "4px" }} />
      </Box>
    );

    switch (sectionId) {
      case "workExperience":
        return workExperiences && workExperiences.length ? (
          <SectionBox key="workExperience">
            <SectionTitle title="Experience" />
            <Stack spacing={`${paraGap}px`}>
              {workExperiences.map((w, i) => (
                <Box key={i}>
                  <Box style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "2px" }}>
                    <Typography style={getTextStyle({ fontWeight: "bold", color: color, fontSize: "12px" }, isSidebar)}>
                      {w.jobTitle}
                    </Typography>
                    <Typography style={getTextStyle({ fontSize: "10px", color: "gray" }, isSidebar)}>
                      {formatDate(w.startMonth, w.startYear)} – {w.current ? "Present" : formatDate(w.endMonth, w.endYear)}
                    </Typography>
                  </Box>
                  <Typography style={getTextStyle({ fontWeight: 600, fontSize: "11px", marginBottom: "4px" }, isSidebar)}>
                    {w.employer} {w.location ? `| ${w.location}` : ""}
                  </Typography>
                  {w.description && (
                    <Box
                      style={getTextStyle({ fontSize: "10px", color: "#555", textAlign: "justify" }, isSidebar)}
                      dangerouslySetInnerHTML={{
                        __html: w.description
                          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                          .replace(/\*(.*?)\*/g,     "<em>$1</em>")
                          .replace(/\n/g,             "<br/>"),
                      }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "skills":
        return (savedSkills.length || softwareSkills.length) ? (
          <SectionBox key="skills">
            <SectionTitle title="Skills" />
            <Grid container spacing={1}>
              {(savedSkills.length ? savedSkills : (data.skills || [])).map((sk, i) => {
                const name = typeof sk === "string" ? sk : sk.name;
                return (
                  <Grid item xs={6} key={i}>
                    <Typography style={getTextStyle({ fontSize: "11px" }, isSidebar)}>• {name}</Typography>
                  </Grid>
                );
              })}
              {softwareSkills.map((s, i) => (
                <Grid item xs={6} key={`sw-${i}`}>
                  <Typography style={getTextStyle({ fontSize: "11px" }, isSidebar)}>• {s.name} - {s.level}%</Typography>
                </Grid>
              ))}
            </Grid>
          </SectionBox>
        ) : null;

      case "references":
        return savedReferences.length ? (
          <SectionBox key="references">
            <SectionTitle title="References" />
            <Grid container spacing={2}>
              {savedReferences.map((ref, i) => (
                <Grid item xs={6} key={i} style={{ marginBottom: `${paraGap}px` }}>
                  <Typography style={getTextStyle({ fontWeight: "bold", fontSize: "12px", color: color }, isSidebar)}>
                    {ref.name || ref}
                  </Typography>
                  {ref.employer && <Typography style={getTextStyle({ fontSize: "10px", color: "gray" }, isSidebar)}>{ref.employer} {ref.position && `/ ${ref.position}`}</Typography>}
                  {ref.phone && <Typography style={getTextStyle({ fontSize: "10px" }, isSidebar)}>Phone: {ref.phone}</Typography>}
                  {ref.email && <Typography style={getTextStyle({ fontSize: "10px" }, isSidebar)}>Email: {ref.email}</Typography>}
                </Grid>
              ))}
            </Grid>
          </SectionBox>
        ) : null;

      case "contact":
        return (
          <SectionBox key="contact">
            {isSidebar && photo && (
              <Box style={{ display: "flex", justifyContent: "center", marginBottom: `${paraGap}px`, marginTop: "8px" }}>
                <Avatar
                  src={photo}
                  style={{ width: 120, height: 120, border: `${lineWeight}px solid ${color}`, boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}
                />
              </Box>
            )}
            
            {(savedSummary || data.profile) && (
              <Box>
                <SectionTitle title="About Me" />
                <Typography style={getTextStyle({ fontSize: "11px", textAlign: "justify" }, isSidebar)}>
                  {savedSummary || data.profile}
                </Typography>
              </Box>
            )}
          </SectionBox>
        );

      case "education":
        return educationEntries.length ? (
          <SectionBox key="education">
            <SectionTitle title="Education" />
            <Stack spacing={`${paraGap}px`}>
              {educationEntries.map((edu, i) => {
                const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
                const dateRange = edu.educationLevel === "school" ? `${edu.startYear || ""} - ${edu.endYear || ""}` : `${edu.startYear || ""} - ${edu.gradYear || ""}`;
                return (
                  <Box key={i}>
                    <Typography style={getTextStyle({ fontWeight: "bold", fontSize: "12px", color: color }, isSidebar)}>
                      {degreeName} {fieldOfStudy && `| ${fieldOfStudy}`}
                    </Typography>
                    <Typography style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                      {institutionName}
                    </Typography>
                    <Typography style={getTextStyle({ fontSize: "10px", color: "gray" }, isSidebar)}>
                      {edu.gradYear || edu.endYear ? (edu.gradYear || edu.endYear) : dateRange}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </SectionBox>
        ) : null;

      case "languages":
        return savedLanguages.length ? (
          <SectionBox key="languages">
            <SectionTitle title="Language" />
            <Stack spacing="4px">
              {savedLanguages.map((l, i) => (
                <Typography key={i} style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                  • {l.name}{l.level ? ` (${l.level})` : ""}
                </Typography>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "accomplishments":
        return savedAccomplishments.length ? (
          <SectionBox key="accomplishments">
            <SectionTitle title="Awards" />
            <Stack spacing="4px">
              {savedAccomplishments.map((a, i) => (
                <Typography key={i} style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                  • {a}
                </Typography>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "certifications":
        return savedCertifications.length ? (
          <SectionBox key="certifications">
            <SectionTitle title="Certifications" />
            <Stack spacing="4px">
              {savedCertifications.map((c, i) => (
                <Typography key={i} style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                  • {c.name}{c.provider ? ` – ${c.provider}` : ""}{c.year ? ` (${c.year})` : ""}
                </Typography>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "volunteering":
        return savedVolunteering.length ? (
          <SectionBox key="volunteering">
            <SectionTitle title="Volunteering" />
            <Stack spacing={`${paraGap}px`}>
              {savedVolunteering.map((v, i) => (
                <Box key={i}>
                  <Typography style={getTextStyle({ fontWeight: "bold", fontSize: "11px" }, isSidebar)}>{v.subtopic}</Typography>
                  <Typography style={getTextStyle({ fontSize: "10px", opacity: 0.75 }, isSidebar)}>{v.fromDate} - {v.toDate}</Typography>
                  <Typography style={getTextStyle({ fontSize: "11px" }, isSidebar)}>{v.content}</Typography>
                </Box>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "interests":
        return savedInterests.length ? (
          <SectionBox key="interests">
            <SectionTitle title="Interests" />
            <Box style={{ display: "flex", flexWrap: "wrap", gap: `4px ${Math.round(paraGap * 0.5)}px` }}>
              {savedInterests.map((item, i) => (
                <Typography key={i} style={getTextStyle({ fontSize: "11px" }, isSidebar)}>• {item}</Typography>
              ))}
            </Box>
          </SectionBox>
        ) : null;

      case "websites":
        return savedWebsites.length ? (
          <SectionBox key="websites">
            <SectionTitle title="Websites & Profiles" />
            <Stack spacing="4px">
              {savedWebsites.map((site, i) => (
                <Box key={i} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <LinkIcon style={{ fontSize: "14px", color: color }} />
                  <Typography style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                    {site.label ? (
                      <>
                        <span style={{ fontWeight: "bold" }}>{site.label}:</span> {site.url}
                      </>
                    ) : (
                      site.url
                    )}
                    {site.addToHeader && (
                      <span style={{ 
                        fontSize: "8px", 
                        color: color, 
                        fontWeight: "bold",
                        marginLeft: "4px",
                        backgroundColor: `${color}20`,
                        padding: "1px 6px",
                        borderRadius: "3px"
                      }}>
                        Header
                      </span>
                    )}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      case "additionalInfo":
        return savedAdditionalInfo.length ? (
          <SectionBox key="additionalInfo">
            <SectionTitle title="Additional Info" />
            <Stack spacing="4px">
              {savedAdditionalInfo.map((info, i) => (
                <Typography key={i} style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                  • {info}
                </Typography>
              ))}
            </Stack>
          </SectionBox>
        ) : null;

      default:
        if (sectionId.startsWith("custom_")) {
          const name = sectionId.replace("custom_", "");
          const items = customSections[name] || [];
          return items.length ? (
            <SectionBox key={sectionId}>
              <SectionTitle title={name.replace(/_/g, " ")} />
              <Stack spacing="4px">
                {items.map((item, i) => (
                  <Typography key={i} style={getTextStyle({ fontSize: "11px" }, isSidebar)}>
                    • {item}
                  </Typography>
                ))}
              </Stack>
            </SectionBox>
          ) : null;
        }
        return null;
    }
  };

  const availableHeight = page.height - (2 * tbPad);

  const paginateContent = useCallback(() => {
    if (!leftContentRef.current) return;
    const sections = Array.from(leftContentRef.current.querySelectorAll(".resume-section"));
    if (sections.length === 0) { setPages([{ leftIds: [] }]); return; }

    const newPages = [];
    let currentIds = [];
    let currentHeight = 0;

    sections.forEach(section => {
      const id = section.getAttribute("data-section-id");
      const h  = section.offsetHeight;
      const targetAvailableHeight = newPages.length === 0 ? (availableHeight - 220) : availableHeight;

      if (currentHeight + h > targetAvailableHeight && currentIds.length > 0) {
        newPages.push({ leftIds: currentIds });
        currentIds = [id];
        currentHeight = h;
      } else {
        currentIds.push(id);
        currentHeight += h;
      }
    });
    if (currentIds.length > 0) newPages.push({ leftIds: currentIds });
    setPages(newPages);
  }, [
    availableHeight, leftSectionOrder,
    savedSummary, workExperiences, savedLanguages, savedAccomplishments,
    savedCertifications, softwareSkills, savedVolunteering, savedInterests,
    savedReferences, savedAdditionalInfo, savedWebsites, customSections,
    fontSize, headingSize, font, fontStyle, lineSpacing, paragraphSpacing, sectionSpacing,
  ]);

  useEffect(() => {
    const id = setTimeout(() => paginateContent(), 400);
    return () => clearTimeout(id);
  }, [paginateContent]);

  const getSectionTitle = id => {
    const map = {
      contact: "Contact", education: "Education", skills: "Skills",
      profile: "Profile", workExperience: "Work Experience", languages: "Languages",
      accomplishments: "Accomplishments", certifications: "Certifications",
      softwareSkills: "Software Skills", volunteering: "Volunteering",
      interests: "Interests", websites: "Websites/Profiles",
      references: "References", additionalInfo: "Additional Info",
    };
    if (id.startsWith("custom_")) return id.replace("custom_", "").toUpperCase();
    return map[id] || id;
  };

  const pageStyle = {
    width:          `${page.width}px`,
    minHeight:      `${page.height}px`,
    maxWidth:       `${page.width}px`,
    overflow:       "hidden",
    display:        "flex",
    flexDirection:  "column",
    backgroundColor: "#ffffff",
    boxShadow:      exporting ? "none" : "0 4px 20px rgba(0,0,0,0.15)",
    margin:         "0 auto 24px",
    boxSizing:      "border-box",
    fontFamily:     font,
    fontSize:       fs,
  };

  const mainAreaStyle = {
    width:         `${MAIN_W}px`,
    minWidth:      0,
    minHeight:     `${page.height}px`,
    paddingTop:    `${tbPad}px`,
    paddingBottom: `${tbPad}px`,
    paddingLeft:   `${sidePad}px`,
    paddingRight:  `${sidePad}px`,
    boxSizing:     "border-box",
    display:       "flex",
    flexDirection: "column",
    overflow:      "hidden",
  };

  const sidebarStyle = {
    width:           `${SIDEBAR_W}px`,
    minWidth:        `${SIDEBAR_W}px`,
    maxWidth:        `${SIDEBAR_W}px`,
    minHeight:       `${page.height}px`,
    overflow:        "hidden",
    backgroundColor: "#fafafa",
    color:           "#333",
    paddingTop:      `${tbPad}px`,
    paddingBottom:   `${tbPad}px`,
    paddingLeft:     `${Math.max(10, Math.round(sidePad * 0.8))}px`,
    paddingRight:    `${Math.max(10, Math.round(sidePad * 0.8))}px`,
    boxSizing:       "border-box",
    display:         "flex",
    flexDirection:   "column",
    gap:             `${Math.max(8, Math.round(secGap * 0.6))}px`,
    flexShrink:      0,
  };

  const renderDragPanel = (side, sections) => (
    <Box style={{ flex: 1 }}>
      <Typography variant="h6" style={{ marginBottom: "12px", fontWeight: 600 }}>
        {side === "left" ? "Left (Main)" : "Right (Sidebar)"} — {sections.length} sections
      </Typography>
      <Box style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 4, padding: 8 }}>
        {sections.map((id, idx) => (
          <Paper key={id} draggable
            onDragStart={e => mkDragStart(side, sections)(e, idx)}
            onDragOver={e => handleDragOver(e, idx, side)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx, side)}
            onDragEnd={handleDragEnd}
            style={{
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              backgroundColor: dragOverIndex === idx && dragOverSide === side ? "#f0f0f0" : "white",
              border: dragOverIndex === idx && dragOverSide === side
                ? `2px dashed ${color}`
                : "1px solid #ddd",
              cursor: "grab",
            }}>
            <DragIndicator color="action" style={{ marginRight: 8 }} />
            <Typography style={{ flex: 1, fontSize: "13px" }}>{getSectionTitle(id)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        ))}
        {!sections.length && (
          <Typography style={{ padding: 16, textAlign: "center", color: "#888", fontSize: "13px" }}>
            Drag sections here from the other side.
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" style={{ marginTop: 8 }}
        onClick={() => side === "left"
          ? (setLeftSectionOrder(defaultLeftSectionOrder),  localStorage.setItem("resume40LeftSectionOrder",  JSON.stringify(defaultLeftSectionOrder)))
          : (setRightSectionOrder(defaultRightSectionOrder), localStorage.setItem("resume40RightSectionOrder", JSON.stringify(defaultRightSectionOrder)))}>
        Reset {side === "left" ? "Left" : "Right"}
      </Button>
    </Box>
  );

  const totalPages = Math.max(1, pages.length);

  return (
    <Box style={{ position: "relative", fontFamily: font }}>
      {/* Controls */}
      {!exporting && (
        <Box style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap", justifyContent: "center", '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button variant="contained" size="small" startIcon={<Reorder />} onClick={() => setArrangeDialogOpen(true)}>
                Arrange Sections
              </Button>
              <Button variant="outlined" size="small" color="secondary" onClick={resetAll}>
                Reset All
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container — renders ALL left sections to measure heights */}
      <Box
        ref={leftContentRef}
        style={{
          position: "absolute", top: -9999, visibility: "hidden", pointerEvents: "none",
          width: `${MAIN_W}px`,
          paddingLeft: `${sideMargins}px`, paddingRight: `${sideMargins}px`,
          fontFamily: font, fontSize: fs,
          boxSizing: "border-box",
        }}
      >
        {leftSectionOrder.map(id => (
          <React.Fragment key={id}>{renderSection(id, false)}</React.Fragment>
        ))}
      </Box>

      {/* Resume Pages */}
      {(pages.length > 0 ? pages : [{ leftIds: null }]).map((pageContent, pageIdx) => (
        <Box
          key={pageIdx}
          className="resume-page"
          style={{
            ...pageStyle,
            display: exporting ? "flex" : (pageIdx === currentPage ? "flex" : "none"),
          }}
        >
          {/* HEADER SECTION (Spans full width, on Page 1 only) */}
          {pageIdx === 0 && (
            <Box style={{ backgroundColor: color, color: "#fff", padding: "40px 32px 32px 32px" }}>
              <Typography style={{
                fontFamily: font, fontWeight: 900, fontSize: `${32 * headingScale}px`,
                lineHeight: 1.1, color: "white", letterSpacing: "2px", ...overflowText
              }}>
                {((data.firstName || "") + " " + (data.lastName || "")).toUpperCase()}
              </Typography>
              {data.currentPosition && (
                <Typography style={{
                  fontFamily: font, fontSize: `${16 * fontScale}px`, color: "rgba(255,255,255,0.9)",
                  marginTop: "8px", letterSpacing: "1px", ...overflowText
                }}>
                  {data.currentPosition.toUpperCase()}
                </Typography>
              )}
            </Box>
          )}

          {/* CONTACT BAR (Spans full width, on Page 1 only) */}
          {pageIdx === 0 && (
            <Box style={{ padding: "12px 32px", borderBottom: `${lineWeight}px solid #eee` }}>
              <Grid container spacing={1}>
                {data.phone && (
                  <Grid item xs={3}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Phone style={{ fontSize: 12, color: color }} />
                      <Typography style={{ fontFamily: font, fontSize: `${9 * fontScale}px`, ...overflowText }}>{data.phone}</Typography>
                    </Stack>
                  </Grid>
                )}
                {data.email && (
                  <Grid item xs={3}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Email style={{ fontSize: 12, color: color }} />
                      <Typography style={{ fontFamily: font, fontSize: `${9 * fontScale}px`, ...overflowText }}>{data.email}</Typography>
                    </Stack>
                  </Grid>
                )}
                {data.website && (
                  <Grid item xs={3}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Language style={{ fontSize: 12, color: color }} />
                      <Typography style={{ fontFamily: font, fontSize: `${9 * fontScale}px`, ...overflowText }}>{data.website}</Typography>
                    </Stack>
                  </Grid>
                )}
                {data.city && (
                  <Grid item xs={3}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <LocationOn style={{ fontSize: 12, color: color }} />
                      <Typography style={{ fontFamily: font, fontSize: `${9 * fontScale}px`, ...overflowText }}>{data.city}</Typography>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Body Columns */}
          <Box style={{ display: "flex", flex: 1, flexDirection: "row", width: "100%" }}>
            {/* MAIN CONTENT (Left Column) */}
            <Box style={mainAreaStyle}>
              {pageContent.leftIds && pageContent.leftIds.map(id => (
                <React.Fragment key={id}>
                  {renderSection(id, false)}
                </React.Fragment>
              ))}
            </Box>

            {/* SIDEBAR (Right Column) — shown only on Page 1 */}
            <Box style={sidebarStyle}>
              {pageIdx === 0 && rightSectionOrder.map((id, i) => {
                const content = renderSection(id, true);
                if (!content) return null;
                return (
                  <React.Fragment key={id}>
                    {i > 0 && (
                      <Divider style={{ borderColor: "#eee", borderBottomWidth: lineWeight, margin: `${Math.round(paraGap * 0.4)}px 0` }} />
                    )}
                    {content}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Arrange Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Reorder /> Arrange Resume Sections
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Drag to reorder sections or move them between main content and sidebar columns.
          </Typography>
          <Box style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {renderDragPanel("left",  leftSectionOrder)}
            {renderDragPanel("right", rightSectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume40;